import React, { useState } from 'react';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { CreateGoodsReceiptItemData } from '../../types/goodsReceipt';
import { goodsReceiptService } from '../../services/goodsReceiptService';
import { purchaseOrderService } from '../../services/purchaseOrderService';
import { invoiceTemplateService } from '../../services/invoiceTemplateService';
import { api } from '../../services/api';

interface GoodsReceiptFormProps {
  onSuccess: () => void;
  onCancel: () => void;
}

interface Warehouse {
  id: string;
  code: string;
  name: string;
  address?: string;
}

interface GRNItemRow extends CreateGoodsReceiptItemData {
  productName?: string;
  orderedQty?: number;
  receivedSoFar?: number;
  isSerialized?: boolean;
}

// ─── Design System Tokens ────────────────────────────────────────────────────

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '9px 12px',
  background: '#f4efe2',
  border: '1px solid rgba(92,78,51,0.25)',
  borderRadius: 4,
  fontFamily: "'Inter', sans-serif",
  fontSize: 12,
  color: '#5b5a52',
  outline: 'none',
  boxSizing: 'border-box',
  colorScheme: 'light'
};

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontFamily: "'Inter', sans-serif",
  fontSize: 9,
  fontWeight: 500,
  letterSpacing: '0.2em',
  textTransform: 'uppercase',
  color: '#6b6a5c',
  marginBottom: 6
};

const sectionStyle: React.CSSProperties = {
  background: '#ffffff',
  border: '1px solid rgba(92,78,51,0.15)',
  borderRadius: 6,
  padding: 20
};

const sectionHeadingStyle: React.CSSProperties = {
  fontFamily: "'Inter', sans-serif",
  fontSize: 9,
  fontWeight: 500,
  letterSpacing: '0.2em',
  textTransform: 'uppercase',
  color: '#d97706',
  marginBottom: 16,
  paddingBottom: 10,
  borderBottom: '1px solid rgba(92,78,51,0.15)'
};

const primaryBtnStyle: React.CSSProperties = {
  background: 'linear-gradient(135deg, #d97706, #b45309)',
  color: '#1c2033',
  border: 'none',
  borderRadius: 4,
  padding: '10px 24px',
  fontFamily: "'Inter', sans-serif",
  fontSize: 10,
  fontWeight: 500,
  letterSpacing: '0.2em',
  textTransform: 'uppercase',
  cursor: 'pointer'
};

const ghostBtnStyle: React.CSSProperties = {
  background: 'transparent',
  color: '#6b6a5c',
  border: '1px solid rgba(92,78,51,0.25)',
  borderRadius: 4,
  padding: '10px 24px',
  fontFamily: "'Inter', sans-serif",
  fontSize: 10,
  letterSpacing: '0.2em',
  textTransform: 'uppercase',
  cursor: 'pointer'
};

const smallInputStyle: React.CSSProperties = {
  padding: '6px 8px',
  background: '#f4efe2',
  border: '1px solid rgba(92,78,51,0.25)',
  borderRadius: 4,
  fontFamily: "'Inter', sans-serif",
  fontSize: 12,
  color: '#5b5a52',
  outline: 'none',
  boxSizing: 'border-box',
  textAlign: 'right',
  colorScheme: 'light'
};

const thStyle: React.CSSProperties = {
  padding: '8px 10px',
  fontFamily: "'Inter', sans-serif",
  fontSize: 8,
  fontWeight: 400,
  letterSpacing: '0.18em',
  textTransform: 'uppercase',
  color: '#6b6a5c',
  whiteSpace: 'nowrap'
};

// ─── Component ───────────────────────────────────────────────────────────────

export const GoodsReceiptForm: React.FC<GoodsReceiptFormProps> = ({
  onSuccess,
  onCancel
}) => {
  const queryClient = useQueryClient();
  const [errors, setErrors] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [postImmediately, setPostImmediately] = useState(false);

  const [formData, setFormData] = useState({
    purchaseOrderId: '',
    warehouseId: '',
    templateId: '',
    receivedDate: new Date().toISOString().split('T')[0],
    receivedBy: '',
    notes: ''
  });

  const [items, setItems] = useState<GRNItemRow[]>([]);

  // Load purchase orders (only ORDERED or PARTIALLY_RECEIVED)
  const { data: poData, isLoading: poLoading } = useQuery({
    queryKey: ['purchase-orders-for-grn'],
    queryFn: () => purchaseOrderService.getPurchaseOrders({
      limit: 1000,
      status: undefined
    })
  });

  const availablePOs = poData?.purchaseOrders?.filter(
    po => po.status === 'ORDERED' || po.status === 'PARTIALLY_RECEIVED'
  ) || [];

  // Load warehouses
  const { data: warehousesData, isLoading: warehousesLoading } = useQuery({
    queryKey: ['warehouses'],
    queryFn: async () => {
      const response = await api.get('/warehouses');
      return response.data;
    }
  });

  const warehouses: Warehouse[] = warehousesData?.data || [];

  // Load invoice/document templates (shared with Invoice/Estimate/PO)
  const { data: templates, isLoading: templatesLoading } = useQuery({
    queryKey: ['invoice-templates'],
    queryFn: () => invoiceTemplateService.getAll()
  });

  // Default a new GRN to the company's default template once templates load
  React.useEffect(() => {
    if (!formData.templateId && templates && templates.length > 0) {
      const defaultTemplate = templates.find(t => t.isDefault) || templates[0];
      setFormData(prev => ({ ...prev, templateId: defaultTemplate.id }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templates]);

  // Load selected PO details
  const { data: selectedPO } = useQuery({
    queryKey: ['purchase-order', formData.purchaseOrderId],
    queryFn: () => purchaseOrderService.getPurchaseOrder(formData.purchaseOrderId),
    enabled: !!formData.purchaseOrderId
  });

  // When PO is selected, populate items
  React.useEffect(() => {
    if (selectedPO && selectedPO.items) {
      const grnItems: GRNItemRow[] = selectedPO.items.map(item => {
        const remainingQty = item.quantity - item.receivedQuantity;
        return {
          poItemId: item.id,
          productName: item.product?.name || 'N/A',
          orderedQty: item.quantity,
          receivedSoFar: item.receivedQuantity,
          isSerialized: item.product?.isSerialized || false,
          quantityReceived: remainingQty,
          quantityRejected: 0,
          serialNumbers: '',
          notes: ''
        };
      });
      setItems(grnItems);
    } else {
      setItems([]);
    }
  }, [selectedPO]);

  // Create mutation
  const createMutation = useMutation({
    mutationFn: goodsReceiptService.createGoodsReceipt,
    onSuccess: async (createdGRN) => {
      if (postImmediately) {
        try {
          await goodsReceiptService.postGoodsReceipt(createdGRN.id);
        } catch (error) {
          console.error('Error posting GRN:', error);
        }
      }
      queryClient.invalidateQueries({ queryKey: ['goods-receipts'] });
      queryClient.invalidateQueries({ queryKey: ['purchase-orders'] });
      onSuccess();
    },
    onError: (error: any) => {
      setErrors([error.response?.data?.message || 'Failed to create goods receipt']);
      setIsSubmitting(false);
    }
  });

  // Update item field
  const updateItem = (index: number, field: keyof GRNItemRow, value: any) => {
    const newItems = [...items];
    newItems[index] = { ...newItems[index], [field]: value };
    setItems(newItems);
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors([]);
    setIsSubmitting(true);

    const validationErrors: string[] = [];
    if (!formData.purchaseOrderId) validationErrors.push('Please select a purchase order');
    if (!formData.warehouseId) validationErrors.push('Please select a warehouse');
    if (!formData.receivedDate) validationErrors.push('Please enter received date');
    if (!formData.receivedBy) validationErrors.push('Please enter received by name');
    if (items.length === 0) validationErrors.push('No items to receive');

    items.forEach((item, index) => {
      if (item.quantityReceived < 0) {
        validationErrors.push(`Item ${index + 1}: Quantity received cannot be negative`);
      }
      if (item.quantityRejected && item.quantityRejected < 0) {
        validationErrors.push(`Item ${index + 1}: Quantity rejected cannot be negative`);
      }
      const totalQty = item.quantityReceived + (item.quantityRejected || 0);
      const remainingQty = (item.orderedQty || 0) - (item.receivedSoFar || 0);
      if (totalQty > remainingQty) {
        validationErrors.push(
          `Item ${index + 1}: Total quantity (${totalQty}) exceeds remaining quantity (${remainingQty})`
        );
      }
      if (item.isSerialized && item.quantityReceived > 0 && !item.serialNumbers) {
        validationErrors.push(`Item ${index + 1}: Serial numbers required for serialized products`);
      }
    });

    if (validationErrors.length > 0) {
      setErrors(validationErrors);
      setIsSubmitting(false);
      return;
    }

    const grnData = {
      purchaseOrderId: formData.purchaseOrderId,
      warehouseId: formData.warehouseId,
      templateId: formData.templateId || undefined,
      receivedDate: formData.receivedDate,
      receivedBy: formData.receivedBy,
      items: items.map(item => ({
        poItemId: item.poItemId,
        quantityReceived: item.quantityReceived,
        quantityRejected: item.quantityRejected || 0,
        serialNumbers: item.serialNumbers || undefined,
        notes: item.notes || undefined
      })),
      notes: formData.notes || undefined
    };

    try {
      await createMutation.mutateAsync(grnData);
    } catch (error) {
      console.error('Form submission error:', error);
    }
  };

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      background: 'rgba(7,7,15,0.85)',
      overflowY: 'auto',
      zIndex: 50,
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'center',
      padding: '40px 16px'
    }}>
      <div style={{
        width: '100%',
        maxWidth: 1100,
        background: '#ffffff',
        border: '1px solid rgba(92,78,51,0.25)',
        borderRadius: 8,
        marginBottom: 40,
        overflow: 'hidden'
      }}>

        {/* ── Header ── */}
        <div style={{
          padding: '20px 28px',
          borderBottom: '1px solid rgba(92,78,51,0.15)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div>
            <div style={{
              fontFamily: "'Inter', sans-serif",
              fontSize: 9,
              fontWeight: 500,
              letterSpacing: '0.25em',
              textTransform: 'uppercase',
              color: '#6b6a5c',
              marginBottom: 6
            }}>
              Inventory
            </div>
            <h3 style={{
              fontFamily: "'Inter', sans-serif",
              fontSize: 22,
              fontWeight: 300,
              color: '#1c2033',
              margin: 0,
              letterSpacing: '0.04em'
            }}>
              Create Goods Receipt
            </h3>
          </div>
          <button
            onClick={onCancel}
            style={{
              background: 'transparent',
              border: '1px solid rgba(92,78,51,0.25)',
              borderRadius: 4,
              color: '#6b6a5c',
              width: 32,
              height: 32,
              cursor: 'pointer',
              fontFamily: "'Inter', sans-serif",
              fontSize: 16,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            &times;
          </button>
        </div>

        <div style={{ padding: 28, display: 'flex', flexDirection: 'column', gap: 20 }}>

          {/* ── Error Panel ── */}
          {errors.length > 0 && (
            <div style={{
              background: 'rgba(201,90,90,0.08)',
              border: '1px solid rgba(201,90,90,0.2)',
              borderRadius: 4,
              padding: 12
            }}>
              <ul style={{
                margin: 0,
                paddingLeft: 16,
                fontFamily: "'Inter', sans-serif",
                fontSize: 12,
                color: '#c95a5a'
              }}>
                {errors.map((error, index) => (
                  <li key={index}>{error}</li>
                ))}
              </ul>
            </div>
          )}

          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

            {/* ── Receipt Details ── */}
            <div style={sectionStyle}>
              <div style={sectionHeadingStyle}>Receipt Details</div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>

                {/* Purchase Order */}
                <div>
                  <label style={labelStyle}>
                    Purchase Order <span style={{ color: '#c95a5a' }}>*</span>
                  </label>
                  <select
                    value={formData.purchaseOrderId}
                    onChange={(e) => setFormData({ ...formData, purchaseOrderId: e.target.value })}
                    disabled={isSubmitting || poLoading}
                    style={inputStyle}
                    required
                  >
                    <option value="">
                      {poLoading
                        ? 'Loading purchase orders...'
                        : availablePOs.length === 0
                        ? 'No purchase orders available'
                        : 'Select a purchase order'}
                    </option>
                    {availablePOs.map((po) => (
                      <option key={po.id} value={po.id}>
                        {po.poNumber} - {po.vendor?.name} ({po.status})
                      </option>
                    ))}
                  </select>
                  {!poLoading && availablePOs.length === 0 && poData?.purchaseOrders && poData.purchaseOrders.length > 0 && (
                    <p style={{
                      fontFamily: "'Inter', sans-serif",
                      fontSize: 11,
                      color: '#d97706',
                      marginTop: 4,
                      marginBottom: 0
                    }}>
                      Note: {poData.purchaseOrders.length} PO(s) exist but none are ORDERED or PARTIALLY_RECEIVED.
                    </p>
                  )}
                </div>

                {/* Warehouse */}
                <div>
                  <label style={labelStyle}>
                    Warehouse <span style={{ color: '#c95a5a' }}>*</span>
                  </label>
                  <select
                    value={formData.warehouseId}
                    onChange={(e) => setFormData({ ...formData, warehouseId: e.target.value })}
                    disabled={isSubmitting || warehousesLoading}
                    style={inputStyle}
                    required
                  >
                    <option value="">
                      {warehousesLoading ? 'Loading warehouses...' : 'Select a warehouse'}
                    </option>
                    {warehouses.map((warehouse) => (
                      <option key={warehouse.id} value={warehouse.id}>
                        {warehouse.code} - {warehouse.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Template */}
                <div>
                  <label style={labelStyle}>Document Template</label>
                  <select
                    value={formData.templateId}
                    onChange={(e) => setFormData({ ...formData, templateId: e.target.value })}
                    disabled={isSubmitting || templatesLoading}
                    style={inputStyle}
                  >
                    <option value="">
                      {templatesLoading ? 'Loading templates...' : 'Default template'}
                    </option>
                    {templates?.map((template) => (
                      <option key={template.id} value={template.id}>
                        {template.name} - {template.templateCompanyName}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Received Date */}
                <div>
                  <label style={labelStyle}>
                    Received Date <span style={{ color: '#c95a5a' }}>*</span>
                  </label>
                  <input
                    type="date"
                    value={formData.receivedDate}
                    onChange={(e) => setFormData({ ...formData, receivedDate: e.target.value })}
                    disabled={isSubmitting}
                    style={inputStyle}
                    required
                  />
                </div>

                {/* Received By */}
                <div>
                  <label style={labelStyle}>
                    Received By <span style={{ color: '#c95a5a' }}>*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.receivedBy}
                    onChange={(e) => setFormData({ ...formData, receivedBy: e.target.value })}
                    disabled={isSubmitting}
                    placeholder="Enter name"
                    style={inputStyle}
                    required
                  />
                </div>
              </div>
            </div>

            {/* ── Items to Receive ── */}
            {items.length > 0 && (
              <div style={sectionStyle}>
                <div style={sectionHeadingStyle}>Items to Receive</div>

                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 900 }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid rgba(92,78,51,0.25)' }}>
                        {[
                          { label: 'Product',        align: 'left'  },
                          { label: 'Ordered',         align: 'right' },
                          { label: 'Received',        align: 'right' },
                          { label: 'Remaining',       align: 'right' },
                          { label: 'Qty Received *',  align: 'right' },
                          { label: 'Qty Rejected',    align: 'right' },
                          { label: 'Serial Numbers',  align: 'left'  },
                          { label: 'Notes',           align: 'left'  }
                        ].map(({ label, align }) => (
                          <th key={label} style={{ ...thStyle, textAlign: align as React.CSSProperties['textAlign'] }}>
                            {label}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((item, index) => {
                        const remainingQty = (item.orderedQty || 0) - (item.receivedSoFar || 0);

                        return (
                          <tr key={index} style={{ borderBottom: '1px solid rgba(239,232,213,0.6)' }}>

                            {/* Product */}
                            <td style={{
                              padding: '10px',
                              fontFamily: "'Inter', sans-serif",
                              fontSize: 12,
                              color: '#5b5a52'
                            }}>
                              {item.productName}
                              {item.isSerialized && (
                                <span style={{
                                  marginLeft: 8,
                                  fontFamily: "'Inter', sans-serif",
                                  fontSize: 8,
                                  fontWeight: 500,
                                  letterSpacing: '0.12em',
                                  background: 'rgba(92,78,51,0.2)',
                                  color: '#d97706',
                                  border: '1px solid rgba(92,78,51,0.3)',
                                  borderRadius: 3,
                                  padding: '2px 6px'
                                }}>
                                  Serialized
                                </span>
                              )}
                            </td>

                            {/* Ordered */}
                            <td style={{
                              padding: '10px',
                              fontFamily: "'Inter', sans-serif",
                              fontSize: 12,
                              color: '#5b5a52',
                              textAlign: 'right'
                            }}>
                              {item.orderedQty}
                            </td>

                            {/* Received So Far */}
                            <td style={{
                              padding: '10px',
                              fontFamily: "'Inter', sans-serif",
                              fontSize: 12,
                              color: '#6b6a5c',
                              textAlign: 'right'
                            }}>
                              {item.receivedSoFar}
                            </td>

                            {/* Remaining */}
                            <td style={{
                              padding: '10px',
                              fontFamily: "'Inter', sans-serif",
                              fontSize: 14,
                              color: '#d97706',
                              textAlign: 'right',
                              fontWeight: 500
                            }}>
                              {remainingQty}
                            </td>

                            {/* Quantity Received */}
                            <td style={{ padding: '6px 10px' }}>
                              <input
                                type="number"
                                value={item.quantityReceived}
                                onChange={(e) => updateItem(index, 'quantityReceived', parseFloat(e.target.value) || 0)}
                                disabled={isSubmitting}
                                min="0"
                                max={remainingQty}
                                step="1"
                                style={{ ...smallInputStyle, width: 72 }}
                                required
                              />
                            </td>

                            {/* Quantity Rejected */}
                            <td style={{ padding: '6px 10px' }}>
                              <input
                                type="number"
                                value={item.quantityRejected || 0}
                                onChange={(e) => updateItem(index, 'quantityRejected', parseFloat(e.target.value) || 0)}
                                disabled={isSubmitting}
                                min="0"
                                step="1"
                                style={{ ...smallInputStyle, width: 72 }}
                              />
                            </td>

                            {/* Serial Numbers */}
                            <td style={{ padding: '6px 10px' }}>
                              <input
                                type="text"
                                value={item.serialNumbers || ''}
                                onChange={(e) => updateItem(index, 'serialNumbers', e.target.value)}
                                disabled={isSubmitting || !item.isSerialized}
                                placeholder={item.isSerialized ? 'SN001,SN002' : 'N/A'}
                                style={{
                                  ...inputStyle,
                                  textAlign: 'left',
                                  opacity: !item.isSerialized ? 0.4 : 1,
                                  width: 120
                                }}
                              />
                            </td>

                            {/* Notes */}
                            <td style={{ padding: '6px 10px' }}>
                              <input
                                type="text"
                                value={item.notes || ''}
                                onChange={(e) => updateItem(index, 'notes', e.target.value)}
                                disabled={isSubmitting}
                                placeholder="Optional notes"
                                style={{ ...inputStyle, textAlign: 'left', width: 140 }}
                              />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* ── Additional Notes ── */}
            <div style={sectionStyle}>
              <div style={sectionHeadingStyle}>Additional Notes</div>
              <div>
                <label style={labelStyle}>Notes</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  disabled={isSubmitting}
                  rows={3}
                  placeholder="Additional notes about this goods receipt..."
                  style={{ ...inputStyle, resize: 'vertical', minHeight: 72 }}
                />
              </div>
            </div>

            {/* ── Post Immediately ── */}
            <div style={sectionStyle}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={postImmediately}
                  onChange={(e) => setPostImmediately(e.target.checked)}
                  style={{ accentColor: '#d97706', width: 14, height: 14 }}
                />
                <span style={{
                  fontFamily: "'Inter', sans-serif",
                  fontSize: 13,
                  color: '#5b5a52'
                }}>
                  Post immediately (update inventory)
                </span>
              </label>
              <p style={{
                fontFamily: "'Inter', sans-serif",
                fontSize: 11,
                color: '#6b6a5c',
                marginTop: 6,
                marginBottom: 0,
                marginLeft: 24
              }}>
                If unchecked, the goods receipt will be saved as DRAFT and can be posted later
              </p>
            </div>

            {/* ── Form Actions ── */}
            <div style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 12,
              paddingTop: 8,
              borderTop: '1px solid rgba(92,78,51,0.15)'
            }}>
              <button
                type="button"
                onClick={onCancel}
                disabled={isSubmitting}
                style={ghostBtnStyle}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting || items.length === 0}
                style={{
                  ...primaryBtnStyle,
                  opacity: isSubmitting || items.length === 0 ? 0.5 : 1,
                  cursor: isSubmitting || items.length === 0 ? 'not-allowed' : 'pointer'
                }}
              >
                {isSubmitting ? 'Saving...' : postImmediately ? 'Create & Post' : 'Create Draft'}
              </button>
            </div>

          </form>
        </div>
      </div>
    </div>
  );
};
